import { useMemo } from 'react'

import { useOrganizationAccess } from './useOrganizationAccess'
import { useUserCredits } from './useCredits'

import { ORGANIZATION, TEAM_SEAT, FREE_TIER } from '@/constants/plansV2/plans.constants'
import {
  ASSESSMENTS,
  DISTRICT_LIBRARY,
  GRADEBOOK,
  STUDENT_PORTFOLIOS,
  GOOGLE_CLASSROOM_GRADE_PASSBACK,
} from '@/constants/plansV2/access.constants'

/**
 * \@description Returns the permissions for the user based on the organization data and user credits
 * @returns \{
 *  hasStudentPortfolios: boolean,
 *  hasGradebook: boolean,
 *  hasAssessments: boolean,
 *  hasDistrictLibrary: boolean,
 *  hasGoogleClassroomGradePassback: boolean,
 * \}
 *
 */
interface Permissions {
  permissions: string[]
  allocationSource: 'organization' | 'team_seat' | 'free_tier'
}

export const usePermissionsV2 = () => {
  const { data: organizationData } = useOrganizationAccess()
  const { data: userCredits } = useUserCredits()

  const { permissions, allocationSource }: Permissions = useMemo(() => {
    if (organizationData?.hasUnlimitedAccess) {
      return { permissions: ORGANIZATION, allocationSource: 'organization' }
    } else if (userCredits?.allocationSource === 'team_seat') {
      return { permissions: TEAM_SEAT, allocationSource: 'team_seat' }
    }

    return { permissions: FREE_TIER, allocationSource: 'free_tier' }
  }, [organizationData?.hasUnlimitedAccess, userCredits?.allocationSource])

  return {
    hasStudentPortfolios: permissions?.includes(STUDENT_PORTFOLIOS),
    hasGradebook: permissions?.includes(GRADEBOOK),
    hasAssessments: permissions?.includes(ASSESSMENTS),
    hasDistrictLibrary: permissions?.includes(DISTRICT_LIBRARY),
    hasGoogleClassroomGradePassback: permissions?.includes(GOOGLE_CLASSROOM_GRADE_PASSBACK),
    allocationSource,
    permissions,
  }
}
